import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useFamilyStore } from '../store/familyStore';
import { useEventStore } from '../store/eventStore';
import { useTodoStore } from '../store/todoStore';
import { useWeatherStore } from '../store/weatherStore';
import { useThemeStore } from '../store/themeStore';
import { getDesignTokens } from '../utils/designTokens';
import { isToday } from '../utils/calendar';
import { formatTime } from '../utils/time';
import { Icon } from '../utils/icons';
import { CoreNavRail } from '../components/CoreNavRail';
import { CalendarEvent } from '../types/calendar';
import { Todo } from '../types/todo';
import { useToastStore } from '../store/toastStore';

export default function Today() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const { family, fetchFamily, isLoading: isFamilyLoading } = useFamilyStore();
  const rawEvents = useEventStore((state) => state.events);
  const fetchEvents = useEventStore((state) => state.fetchEvents);
  const { todos, fetchTodos } = useTodoStore();
  const { weather, fetchWeather, isLoading: isWeatherLoading } = useWeatherStore();
  const { isDarkMode } = useThemeStore();
  const { showToast } = useToastStore();
  const tokens = getDesignTokens(isDarkMode);

  // Memoize today to prevent infinite loops - only recalculate once per day
  const today = useMemo(() => {
    const date = new Date();
    date.setHours(12, 0, 0, 0);
    return date;
  }, []); // Empty deps - only calculate once on mount

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Fetch data
  useEffect(() => {
    if (isAuthenticated) {
      fetchFamily().catch((error) => {
        console.error('Error fetching family:', error);
        showToast(error instanceof Error ? error.message : 'Failed to load family', 'error');
      });
      
      // Fetch events for today (with some buffer for all-day events)
      const startDate = new Date(today);
      startDate.setDate(startDate.getDate() - 1);
      const endDate = new Date(today);
      endDate.setDate(endDate.getDate() + 1);
      
      fetchEvents(
        startDate.toISOString().split('T')[0],
        endDate.toISOString().split('T')[0]
      ).catch((error) => {
        console.error('Error fetching events:', error);
        showToast(error instanceof Error ? error.message : 'Failed to load events', 'error');
      });
      
      fetchTodos().catch((error) => {
        console.error('Error fetching todos:', error);
        showToast(error instanceof Error ? error.message : 'Failed to load todos', 'error');
      });
      fetchWeather('family').catch((error) => {
        console.error('Error fetching weather:', error);
        showToast(error instanceof Error ? error.message : 'Failed to load weather', 'error');
      });
    }
  }, [isAuthenticated, fetchFamily, fetchEvents, fetchTodos, fetchWeather, today, showToast]);

  // Filter events for today
  const todayEvents = useMemo(() => {
    return rawEvents.filter((event) => isToday(event.startTime));
  }, [rawEvents]);

  // Filter and group events/todos by member
  const memberData = useMemo(() => {
    if (!family?.members) return [];

    const dayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
    const dayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

    return family.members.map((member) => {
      // Filter events for this member
      const memberEvents = todayEvents
        .filter((event) => {
          // Check if member is in event.memberIds or event.members
          if (event.memberIds && event.memberIds.includes(member.userId)) {
            return true;
          }
          if (event.members && event.members.some((m) => m.memberId === member.userId)) {
            return true;
          }
          return false;
        })
        .sort((a, b) => a.startTime.getTime() - b.startTime.getTime());

      // Filter todos for this member
      const memberTodos = todos
        .filter((todo) => {
          // Only open todos
          if (todo.status === 'done') return false;
          
          // Must be assigned to this member
          if (todo.assigneeId !== member.userId) return false;
          
          // Must have due date
          if (!todo.dueDateTime) return false;
          
          // Parse due date
          let dueDate: Date;
          if (todo.dueDateTime instanceof Date) {
            dueDate = todo.dueDateTime;
          } else if (typeof todo.dueDateTime === 'string') {
            const dueStr = todo.dueDateTime;
            if (dueStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
              const [year, month, day] = dueStr.split('-').map(Number);
              dueDate = new Date(year, month - 1, day);
            } else {
              const isoDate = new Date(dueStr);
              dueDate = new Date(isoDate.getUTCFullYear(), isoDate.getUTCMonth(), isoDate.getUTCDate());
            }
          } else {
            return false;
          }
          
          const dueDateStart = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate(), 0, 0, 0);
          // Include today's todos and past due todos
          return dueDateStart.getTime() <= dayEnd.getTime();
        })
        .sort((a, b) => {
          // Sort by priority (critical > high > medium > low), then by due date
          const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
          const aPriority = priorityOrder[a.priority || 'medium'];
          const bPriority = priorityOrder[b.priority || 'medium'];
          if (aPriority !== bPriority) {
            return aPriority - bPriority;
          }
          if (a.dueDateTime && b.dueDateTime) {
            const aTime = a.dueDateTime instanceof Date ? a.dueDateTime : new Date(a.dueDateTime);
            const bTime = b.dueDateTime instanceof Date ? b.dueDateTime : new Date(b.dueDateTime);
            return aTime.getTime() - bTime.getTime();
          }
          return 0;
        });

      return {
        member,
        events: memberEvents,
        todos: memberTodos,
      };
    });
  }, [family?.members, todayEvents, todos, today]);

  // Get today's forecast for weather display
  const todayForecast = useMemo(() => {
    if (!weather?.forecast) return null;
    const todayStr = today.toISOString().split('T')[0];
    return weather.forecast.find((day) => day.date === todayStr);
  }, [weather, today]);

  if (!isAuthenticated) {
    return null;
  }

  if (isFamilyLoading && !family) {
    return (
      <div className={`min-h-screen ${tokens.bg.gradient} flex items-center justify-center`}>
        <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${tokens.border.accent}`}></div>
      </div>
    );
  }

  if (!family) {
    return (
      <div className={`min-h-screen ${tokens.bg.gradient} flex items-center justify-center`}>
        <div className={`${tokens.card.bg} ${tokens.card.border} border rounded-landing p-6 ${tokens.card.shadow}`}>
          <p className={tokens.text.primary}>No family found. Please create a family first.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${tokens.bg.gradient} transition-colors duration-200`}>
      <CoreNavRail active="today" />
      
      <div className="pr-20 p-2">
        <div className="max-w-full overflow-x-auto">
          <div className="flex gap-3 min-h-[calc(100vh-4rem)]">
            {/* Weather Column */}
            <div className={`${tokens.card.bg} rounded-landing p-3 border ${tokens.border.light} flex flex-col min-w-[200px] flex-shrink-0`}>
              <div className="mb-3 pb-2 border-b" style={{ borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
                <h4 className={`font-semibold text-sm ${tokens.text.primary} flex items-center gap-2`}>
                  <Icon name="weather" size="sm" className={tokens.text.primary} />
                  Weather
                </h4>
              </div>
              {isWeatherLoading && !weather ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className={`animate-spin rounded-full h-6 w-6 border-b-2 ${tokens.border.accent}`}></div>
                </div>
              ) : weather && todayForecast ? (
                <div className="flex-1 flex flex-col justify-center items-center text-center">
                  <div className={`text-5xl font-bold ${tokens.text.primary} mb-2`}>
                    {weather.current.temperature.fahrenheit}°F
                  </div>
                  <div className={`text-sm ${tokens.text.secondary} mb-1`}>
                    {weather.current.condition}
                  </div>
                  <div className={`text-xs ${tokens.text.muted} mb-4`}>
                    {weather.location.name}, {weather.location.region}
                  </div>
                  <div className={`text-xs ${tokens.text.muted} space-y-1 w-full`}>
                    <div className="flex justify-between">
                      <span>High:</span>
                      <span className="font-semibold">{todayForecast.high.fahrenheit}°F</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Low:</span>
                      <span className="font-semibold">{todayForecast.low.fahrenheit}°F</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={`flex-1 flex items-center justify-center text-xs ${tokens.text.muted}`}>
                  Weather unavailable
                </div>
              )}
            </div>

            {/* Person Columns */}
            {memberData.map(({ member, events, todos }) => (
              <div
                key={member.userId}
                className={`${tokens.card.bg} rounded-landing p-3 border ${tokens.border.light} flex flex-col min-w-[200px] flex-shrink-0`}
              >
                {/* Member Header */}
                <div className="mb-3 pb-2 border-b" style={{ borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
                  <div className="flex items-center gap-2 mb-1">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-sm"
                      style={{ backgroundColor: member.color }}
                    >
                      {member.name.charAt(0)}
                    </div>
                    <span className={`font-semibold text-sm ${tokens.text.primary}`}>{member.name}</span>
                  </div>
                </div>

                {/* Events */}
                {events.length > 0 && (
                  <div className="mb-3">
                    <h4 className={`text-xs font-semibold ${tokens.text.secondary} mb-2`}>Events</h4>
                    <div className="space-y-2">
                      {events.map((event) => (
                        <div
                          key={event.id}
                          className={`rounded-sm p-2 text-xs border-l-2`}
                          style={{
                            borderLeftColor: member.color,
                            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                          }}
                        >
                          <div className={`font-medium ${tokens.text.primary} mb-0.5`}>{event.title}</div>
                          <div className={`${tokens.text.muted} text-[10px]`}>
                            {event.isAllDay ? 'All Day' : formatTime(event.startTime)}
                          </div>
                          {event.location && (
                            <div className={`${tokens.text.muted} text-[10px] mt-0.5`}>{event.location}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Todos */}
                {todos.length > 0 && (
                  <div>
                    <h4 className={`text-xs font-semibold ${tokens.text.secondary} mb-2`}>To Dos</h4>
                    <div className="space-y-1.5">
                      {todos.map((todo) => (
                        <div
                          key={todo.todoId}
                          className={`rounded-sm p-1.5 text-xs flex items-start gap-1.5`}
                          style={{
                            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                          }}
                        >
                          <input type="checkbox" className="mt-0.5" checked={todo.status === 'done'} readOnly />
                          <span className={`${tokens.text.secondary} flex-1`}>{todo.title}</span>
                          {todo.priority === 'high' && (
                            <span className="text-[10px] px-1 py-0.5 bg-orange-500 text-white rounded">!</span>
                          )}
                          {todo.priority === 'critical' && (
                            <span className="text-[10px] px-1 py-0.5 bg-red-500 text-white rounded">!!</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {events.length === 0 && todos.length === 0 && (
                  <div className={`text-xs ${tokens.text.muted} text-center py-4`}>No events or todos</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
